import { useEffect, useMemo, useState } from "react"

import { useNavigate } from "@tanstack/react-router"

import { FoundationLayout } from "@/components/app/foundation-layout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { apiClient, type AccountOnboardingRecord } from "@/lib/api/client"

const STEPS = ["account", "business", "orchard", "rollout"] as const
type OnboardingStep = (typeof STEPS)[number]

const INPUT_CLASS =
  "h-12 rounded-2xl border border-border/70 bg-background/80 px-4 text-sm outline-none transition focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20"

interface OnboardingFormState {
  approximateBlockCount: string
  approximateRowCount: string
  approximateTreeCount: string
  billingEmail: string
  contactPhone: string
  customerCategory: string
  defaultYieldUnit: string
  designation: string
  implementationMode: string
  legalName: string
  name: string
  onboardingOwnerInternal: string
  operatingDistrict: string
  operatingPostalCode: string
  operatingState: string
  operatingTaluk: string
  operatingVillage: string
  orchardCount: string
  preferredLanguage: string
  primaryContactName: string
  primaryCropType: string
  primaryVariety: string
  qrRolloutRequired: boolean
  treeImportMode: string
  phoneNumber: string
}

function toInputString(value: number | string | null | undefined) {
  if (value === undefined || value === null) {
    return ""
  }

  return String(value)
}

function normalizeStep(value: string | null | undefined): OnboardingStep {
  if (value === "business" || value === "orchard" || value === "rollout") {
    return value
  }

  return "account"
}

function buildInitialState(record: AccountOnboardingRecord): OnboardingFormState {
  return {
    approximateBlockCount: toInputString(record.onboardingProfile?.approximateBlockCount),
    approximateRowCount: toInputString(record.onboardingProfile?.approximateRowCount),
    approximateTreeCount: toInputString(record.onboardingProfile?.approximateTreeCount),
    billingEmail: record.organization?.billingEmail ?? "",
    contactPhone: record.organization?.contactPhone ?? "",
    customerCategory: record.organization?.customerCategory ?? "",
    defaultYieldUnit: record.onboardingProfile?.defaultYieldUnit ?? "kg",
    designation: record.user?.designation ?? "",
    implementationMode: record.onboardingProfile?.implementationMode ?? "growbetter_assisted",
    legalName: record.organization?.legalName ?? "",
    name: record.user?.name ?? "",
    onboardingOwnerInternal: record.organization?.onboardingOwnerInternal ?? "",
    operatingDistrict: record.organization?.operatingDistrict ?? "",
    operatingPostalCode: record.organization?.operatingPostalCode ?? "",
    operatingState: record.organization?.operatingState ?? "",
    operatingTaluk: record.organization?.operatingTaluk ?? "",
    operatingVillage: record.organization?.operatingVillage ?? "",
    orchardCount: toInputString(record.onboardingProfile?.orchardCount),
    phoneNumber: record.user?.phoneNumber ?? "",
    preferredLanguage:
      record.user?.preferredLanguage
      ?? record.onboardingProfile?.preferredLanguage
      ?? "en",
    primaryContactName: record.organization?.primaryContactName ?? "",
    primaryCropType: record.onboardingProfile?.primaryCropType ?? "",
    primaryVariety: record.onboardingProfile?.primaryVariety ?? "",
    qrRolloutRequired: record.onboardingProfile?.qrRolloutRequired ?? true,
    treeImportMode: record.onboardingProfile?.treeImportMode ?? "assisted_csv_import",
  }
}

type OnboardingState =
  | { status: "loading" }
  | {
      currentStep: OnboardingStep
      form: OnboardingFormState
      record: AccountOnboardingRecord
      status: "ready"
    }
  | {
      message: string
      status: "error"
    }

function getStepIndex(step: OnboardingStep) {
  return STEPS.indexOf(step)
}

export function AccountOnboardingPage() {
  const navigate = useNavigate()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [state, setState] = useState<OnboardingState>({
    status: "loading",
  })

  useEffect(() => {
    let cancelled = false

    void apiClient
      .getAccountOnboarding()
      .then((response) => {
        if (cancelled) {
          return
        }

        const record = response.data
        const initialStep = normalizeStep(record.onboardingProfile?.completedStep)

        setState({
          currentStep: initialStep,
          form: buildInitialState(record),
          record,
          status: "ready",
        })
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return
        }

        setState({
          message:
            error instanceof Error
              ? error.message
              : "Unable to load orchard onboarding.",
          status: "error",
        })
      })

    return () => {
      cancelled = true
    }
  }, [])

  const currentStepIndex =
    state.status === "ready" ? getStepIndex(state.currentStep) : 0

  const canGoBack = currentStepIndex > 0
  const canGoForward = state.status === "ready" && currentStepIndex < STEPS.length - 1

  const headingDescription = useMemo(() => {
    if (state.status !== "ready") {
      return "Finish your first-time orchard setup after accepting the GrowBetter invitation."
    }

    return `${state.record.organization?.name ?? "Your orchard account"} is ready for setup. Complete the orchard admin profile, business baseline, orchard profile, and rollout plan before go-live.`
  }, [state])

  function updateForm(
    updater: (current: OnboardingFormState) => OnboardingFormState
  ) {
    setState((current) => {
      if (current.status !== "ready") {
        return current
      }

      return {
        ...current,
        form: updater(current.form),
      }
    })
  }

  async function persist(nextStep: OnboardingStep, markComplete = false) {
    if (state.status !== "ready") {
      return
    }

    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      const response = await apiClient.updateAccountOnboarding({
        completedStep: markComplete ? "complete" : nextStep,
        markComplete,
        onboardingProfile: {
          approximateBlockCount: state.form.approximateBlockCount.trim()
            ? Number.parseInt(state.form.approximateBlockCount, 10)
            : null,
          approximateRowCount: state.form.approximateRowCount.trim()
            ? Number.parseInt(state.form.approximateRowCount, 10)
            : null,
          approximateTreeCount: state.form.approximateTreeCount.trim()
            ? Number.parseInt(state.form.approximateTreeCount, 10)
            : null,
          defaultYieldUnit: state.form.defaultYieldUnit.trim() || null,
          implementationMode: state.form.implementationMode.trim() || null,
          orchardCount: state.form.orchardCount.trim()
            ? Number.parseInt(state.form.orchardCount, 10)
            : null,
          preferredLanguage: state.form.preferredLanguage.trim() || null,
          primaryCropType: state.form.primaryCropType.trim() || null,
          primaryVariety: state.form.primaryVariety.trim() || null,
          qrRolloutRequired: state.form.qrRolloutRequired,
          treeImportMode: state.form.treeImportMode.trim() || null,
        },
        organization: {
          billingEmail: state.form.billingEmail.trim() || null,
          contactPhone: state.form.contactPhone.trim() || null,
          customerCategory: state.form.customerCategory.trim() || null,
          legalName: state.form.legalName.trim() || null,
          onboardingOwnerInternal: state.form.onboardingOwnerInternal.trim() || null,
          operatingDistrict: state.form.operatingDistrict.trim() || null,
          operatingPostalCode: state.form.operatingPostalCode.trim() || null,
          operatingState: state.form.operatingState.trim() || null,
          operatingTaluk: state.form.operatingTaluk.trim() || null,
          operatingVillage: state.form.operatingVillage.trim() || null,
          primaryContactName: state.form.primaryContactName.trim() || null,
        },
        user: {
          designation: state.form.designation.trim() || null,
          name: state.form.name.trim() || null,
          phoneNumber: state.form.phoneNumber.trim() || null,
          preferredLanguage: state.form.preferredLanguage.trim() || null,
        },
      })

      if (markComplete) {
        void navigate({
          to: "/orchards",
          replace: true,
        })
        return
      }

      setState({
        currentStep: nextStep,
        form: buildInitialState(response.data),
        record: response.data,
        status: "ready",
      })
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to update orchard onboarding."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <FoundationLayout
      title="GrowBetter orchard setup"
      description={headingDescription}
      links={[]}
    >
      <section className="grid gap-6 xl:grid-cols-[18rem_minmax(0,1fr)]">
        <Card className="rounded-4xl border-border/70 bg-background/80">
          <CardHeader>
            <CardTitle>Setup steps</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            {STEPS.map((step, index) => {
              const labels: Record<OnboardingStep, string> = {
                account: "Orchard admin profile",
                business: "Business profile",
                orchard: "Orchard profile",
                rollout: "Rollout plan",
              }
              const isActive = state.status === "ready" && state.currentStep === step

              return (
                <button
                  key={step}
                  className={`rounded-3xl border px-4 py-3 text-left text-sm transition ${
                    isActive
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border/70 bg-background/70 text-muted-foreground"
                  }`}
                  type="button"
                  onClick={() => {
                    setState((current) => {
                      if (current.status !== "ready") {
                        return current
                      }

                      return {
                        ...current,
                        currentStep: step,
                      }
                    })
                  }}
                >
                  <div className="font-medium">{index + 1}. {labels[step]}</div>
                </button>
              )
            })}
          </CardContent>
        </Card>

        <Card className="rounded-4xl border-border/70 bg-background/80">
          <CardHeader>
            <CardTitle>First-time setup wizard</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {state.status === "loading" ? (
              <div className="rounded-3xl border border-border/70 bg-background/70 px-4 py-5 text-sm text-muted-foreground">
                Loading orchard onboarding…
              </div>
            ) : null}

            {state.status === "error" ? (
              <div className="rounded-3xl border border-destructive/30 bg-destructive/5 px-4 py-5 text-sm text-destructive">
                {state.message}
              </div>
            ) : null}

            {state.status === "ready" ? (
              <>
                {state.currentStep === "account" ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Orchard admin name</span>
                      <input
                        aria-label="Orchard admin name"
                        className={INPUT_CLASS}
                        value={state.form.name}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            name: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Orchard admin email</span>
                      <input
                        aria-label="Orchard admin email"
                        className={`${INPUT_CLASS} bg-muted/40 text-muted-foreground`}
                        readOnly
                        value={state.record.user?.email ?? ""}
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Mobile number</span>
                      <input
                        aria-label="Orchard admin mobile number"
                        className={INPUT_CLASS}
                        value={state.form.phoneNumber}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            phoneNumber: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Designation</span>
                      <input
                        aria-label="Orchard admin designation"
                        className={INPUT_CLASS}
                        value={state.form.designation}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            designation: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Preferred language</span>
                      <input
                        aria-label="Preferred language"
                        className={INPUT_CLASS}
                        value={state.form.preferredLanguage}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            preferredLanguage: event.target.value,
                          }))
                        }
                      />
                    </label>
                  </div>
                ) : null}

                {state.currentStep === "business" ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Legal business name</span>
                      <input
                        aria-label="Legal business name"
                        className={INPUT_CLASS}
                        value={state.form.legalName}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            legalName: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Customer category</span>
                      <input
                        aria-label="Customer category"
                        className={INPUT_CLASS}
                        value={state.form.customerCategory}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            customerCategory: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Primary contact name</span>
                      <input
                        aria-label="Primary contact name"
                        className={INPUT_CLASS}
                        value={state.form.primaryContactName}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            primaryContactName: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Contact phone</span>
                      <input
                        aria-label="Business contact phone"
                        className={INPUT_CLASS}
                        value={state.form.contactPhone}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            contactPhone: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Billing email</span>
                      <input
                        aria-label="Billing email"
                        className={INPUT_CLASS}
                        value={state.form.billingEmail}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            billingEmail: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">GrowBetter onboarding lead</span>
                      <input
                        aria-label="GrowBetter onboarding lead"
                        className={INPUT_CLASS}
                        value={state.form.onboardingOwnerInternal}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            onboardingOwnerInternal: event.target.value,
                          }))
                        }
                      />
                    </label>
                  </div>
                ) : null}

                {state.currentStep === "orchard" ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Village</span>
                      <input
                        aria-label="Operating village"
                        className={INPUT_CLASS}
                        value={state.form.operatingVillage}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            operatingVillage: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Taluk</span>
                      <input
                        aria-label="Operating taluk"
                        className={INPUT_CLASS}
                        value={state.form.operatingTaluk}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            operatingTaluk: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">District</span>
                      <input
                        aria-label="Operating district"
                        className={INPUT_CLASS}
                        value={state.form.operatingDistrict}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            operatingDistrict: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">State</span>
                      <input
                        aria-label="Operating state"
                        className={INPUT_CLASS}
                        value={state.form.operatingState}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            operatingState: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Postal code</span>
                      <input
                        aria-label="Operating postal code"
                        className={INPUT_CLASS}
                        value={state.form.operatingPostalCode}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            operatingPostalCode: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Orchard count</span>
                      <input
                        aria-label="Orchard count"
                        className={INPUT_CLASS}
                        inputMode="numeric"
                        value={state.form.orchardCount}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            orchardCount: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Primary crop type</span>
                      <input
                        aria-label="Primary crop type"
                        className={INPUT_CLASS}
                        value={state.form.primaryCropType}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            primaryCropType: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Primary variety</span>
                      <input
                        aria-label="Primary variety"
                        className={INPUT_CLASS}
                        value={state.form.primaryVariety}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            primaryVariety: event.target.value,
                          }))
                        }
                      />
                    </label>
                  </div>
                ) : null}

                {state.currentStep === "rollout" ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Approximate tree count</span>
                      <input
                        aria-label="Approximate tree count"
                        className={INPUT_CLASS}
                        inputMode="numeric"
                        value={state.form.approximateTreeCount}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            approximateTreeCount: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Approximate block count</span>
                      <input
                        aria-label="Approximate block count"
                        className={INPUT_CLASS}
                        inputMode="numeric"
                        value={state.form.approximateBlockCount}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            approximateBlockCount: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Approximate row count</span>
                      <input
                        aria-label="Approximate row count"
                        className={INPUT_CLASS}
                        inputMode="numeric"
                        value={state.form.approximateRowCount}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            approximateRowCount: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Implementation mode</span>
                      <select
                        aria-label="Implementation mode"
                        className={INPUT_CLASS}
                        value={state.form.implementationMode}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            implementationMode: event.target.value,
                          }))
                        }
                      >
                        <option value="growbetter_assisted">GrowBetter assisted</option>
                        <option value="hybrid_rollout">Hybrid rollout</option>
                        <option value="owner_self_serve">Orchard admin self-serve</option>
                      </select>
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Tree import mode</span>
                      <select
                        aria-label="Tree import mode"
                        className={INPUT_CLASS}
                        value={state.form.treeImportMode}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            treeImportMode: event.target.value,
                          }))
                        }
                      >
                        <option value="assisted_csv_import">Assisted CSV import</option>
                        <option value="manual_registry">Manual registry</option>
                        <option value="owner_csv_import">Orchard admin CSV import</option>
                      </select>
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Default yield unit</span>
                      <input
                        aria-label="Default yield unit"
                        className={INPUT_CLASS}
                        value={state.form.defaultYieldUnit}
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            defaultYieldUnit: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="flex items-center gap-3 rounded-2xl border border-border/70 bg-background/70 px-4 py-3 text-sm">
                      <input
                        aria-label="QR rollout required"
                        checked={state.form.qrRolloutRequired}
                        type="checkbox"
                        onChange={(event) =>
                          updateForm((current) => ({
                            ...current,
                            qrRolloutRequired: event.target.checked,
                          }))
                        }
                      />
                      <span>QR rollout required before go-live</span>
                    </label>
                  </div>
                ) : null}

                {errorMessage ? (
                  <div className="rounded-3xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                    {errorMessage}
                  </div>
                ) : null}

                <div className="flex flex-wrap gap-3">
                  <Button
                    disabled={!canGoBack || isSubmitting}
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (state.status !== "ready" || !canGoBack) {
                        return
                      }

                      setState({
                        ...state,
                        currentStep: STEPS[currentStepIndex - 1],
                      })
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    disabled={isSubmitting}
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (state.status !== "ready") {
                        return
                      }

                      void persist(state.currentStep)
                    }}
                  >
                    {isSubmitting ? "Saving…" : "Save draft"}
                  </Button>
                  {canGoForward ? (
                    <Button
                      disabled={isSubmitting}
                      type="button"
                      onClick={() => {
                        if (state.status !== "ready") {
                          return
                        }

                        void persist(STEPS[currentStepIndex + 1])
                      }}
                    >
                      Continue
                    </Button>
                  ) : (
                    <Button
                      disabled={isSubmitting}
                      type="button"
                      onClick={() => {
                        void persist("rollout", true)
                      }}
                    >
                      {isSubmitting ? "Completing…" : "Complete setup"}
                    </Button>
                  )}
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>
      </section>
    </FoundationLayout>
  )
}
